<template>
    <div class="timer display--flex">
        <div class="timer__counter position--relative">
            <icon-timer
                class="timer__counter__svg"
                v-bind="{ duration: intervalBetweenCalls, color: getColor, ...settings }"
            />
            <transition name="fade" mode="out-in">
                <counter
                    v-if="show.title"
                    :class="[
                        `timer__counter__title color--${getColor}`,
                        isLargeTimer
                            ? 'timer__counter__title--default position--absolute'
                            : 'timer__counter__title--mini'
                    ]"
                    :timeLimit="intervalBetweenCalls"
                    :stop="show.pause"
                    :isNecessaryToEmitSeconds="true"
                    @get-seconds="time = $event"
                    @finished="action"
                />
            </transition>
        </div>
    </div>
</template>

<script>
// packages
import { reactive, toRefs, computed } from '@vue/composition-api';

// store
import { useActions } from '@/hooks/use-store';

// utils
import { haveProperty } from '@/utils/';

// plugins
import i18n from '@/plugins/i18n';

export default {
    components: {
        Counter: () => import( /* webpackChunkName: 'Counter' */ '@/components/bundles/operate/blocks/Counter' ),
        IconTimer: () => import( /* webpackChunkName: 'IconTimer' */ '@/components/bundles/layout/images/icons/IconTimer' )
    },
    props: {
        settings: {
            type: Object,
            default: () => ({})
        },
        intervalBetweenCalls: {
            type: Number,
            default: 60 // 10m
        },
        action: Function
    },
    setup ( props ) {
        // definição das cores nos diversos
        // estados.
        const colorCodes = {
            info: {
                color: 'green'
            },
            alert: {
                color: 'red',
                threshold: 5
            },
            pause: {
                color: 'grey',
                threshold: -1
            }
        };

        // definição das vars dinâmicas
        const state = reactive({
            time: props.intervalBetweenCalls,
            alertIsTrigger: false,
            show: {
                title: false
            }
        });

        // É necessário o timer, devido a animação
        // inicial do timer ( svg ) que dura 1s.
        setTimeout( () => { state.show.title = true; }, 600 );

        /**
         * [computed]
         * Validação se que tipo de timer deve
         * ser apresentado visualmente.
         *
         * @since 1.0.0
         * @category Computed
         * @autor Tiago Martinho
         *
        */
        const isLargeTimer = computed( () => !haveProperty( props.settings, 'loadLargeCounter' ) );

        /**
         * [method]
         * Quando faltar 5 segundos para terminar o tempo
         * é necessário dar o trigger no alerta, de forma
         * a que o utilizador consiga perceber que esta a
         * terminar o tempo.
         *
         * @since 1.0.0
         * @category Method
         * @autor Tiago Martinho
         *
        */
        const triggerAlert = () => {
            // validação se o alerta já está activo.
            // caso esteja, não é necessário activa-lo outra vez.
            if ( state.alertIsTrigger )
                return;

            state.alertIsTrigger = true;
            const { actions } = useActions();
            const type = isLargeTimer.value ? 'thePause' : 'filligOutTheScript';
            actions( 'manageResponse', {
                has: true,
                type: 'insuccess',
                message: i18n.t( 'global.fiveSecondLeftToEnd', { type: i18n.t( `global.${type}` ) })
            });
        };

        /**
         * [computed]
         * Validação que cor o timer timerdeverá ter.
         *
         * @since 1.0.0
         * @category Computed
         * @autor Tiago Martinho
         *
        */
        const getColor = computed( () => {
            const { alert, info, pause } = colorCodes;

            if ( state.time === pause.threshold )
                return pause.color;

            if ( state.time <= alert.threshold ) {
                triggerAlert();
                return alert.color;
            }

            return info.color;
        });

        return {
            ...toRefs( state ),
            getColor,
            isLargeTimer
        };
    }
};
</script>

<style lang="scss">
    .timer {
        @include position-grid( center, center );
        flex-direction: column;
        &__counter {
            &__title {
                &--default {
                    @include typography-specs( 50px, 100px );
                    @include transform-position( 50%, false, false, 50%, translate( -50%, -50% ) );
                    z-index: 2;
                }
                &--mini {
                    @include typography-specs( 22px, 29px );
                    margin-top: 25px;
                }
            }
        }
    }
</style>